/**
 * Service: CRUD de Configuração de Desempenho (Metas + Comissões)
 */

import { supabase } from '@/integrations/supabase/client';
import {
  DesempenhoConfig,
  DesempenhoConfigFormData,
  DesempenhoFaixa,
  DesempenhoFaixaFormData,
  DesempenhoMembro,
  GrupoDesempenho,
  TipoDesempenho,
  resumoMetaFromFaixas,
  vigenciaFromPeriodo,
} from '../types/desempenho';

export class DesempenhoService {
  private async getDiaInicioProducao(tenantId: number): Promise<number> {
    const { data, error } = await supabase
      .from('tenant_settings')
      .select('dia_inicio_producao')
      .eq('tenant_id', tenantId)
      .maybeSingle();

    if (error) throw error;
    return data?.dia_inicio_producao ?? 1;
  }

  /**
   * Configuração vigente para tenant + tipo (+ grupo opcional)
   */
  async buscarConfigVigente(
    tenantId: number,
    tipoDesempenho: TipoDesempenho,
    grupo?: GrupoDesempenho,
    refDate?: string,
    profileId?: string
  ): Promise<DesempenhoConfig | null> {
    const rpcName = profileId
      ? 'get_desempenho_config_aplicavel'
      : 'get_desempenho_config_vigente';
    const { data, error } = await (supabase as any).rpc(rpcName, {
      p_tenant_id: tenantId,
      p_tipo_desempenho: tipoDesempenho,
      p_grupo: grupo ?? null,
      ...(profileId && { p_profile_id: profileId }),
      p_ref_date: refDate ?? new Date().toISOString().split('T')[0],
    });

    if (error) throw error;

    const row = Array.isArray(data) ? data[0] : data;
    if (!row) return null;

    return this.parseRow(row);
  }
  /**
   * Listar todas as configurações de desempenho
   */
  async listar(): Promise<DesempenhoConfig[]> {
    const { data, error } = await supabase
      .from('desempenho_config')
      .select('*')
      .order('grupo', { ascending: true })
      .order('tipo_desempenho', { ascending: true });

    if (error) throw error;

    return (data || []).map(row => this.parseRow(row));
  }

  /**
   * Buscar uma configuração específica
   */
  async buscar(id: string): Promise<DesempenhoConfig> {
    const { data, error } = await supabase
      .from('desempenho_config')
      .select('*')
      .eq('id', Number(id))
      .single();

    if (error) throw error;

    return this.parseRow(data);
  }

  /**
   * Criar nova configuração
   */
  async criar(formData: DesempenhoConfigFormData, tenantId: number): Promise<DesempenhoConfig> {
    const startDay = await this.getDiaInicioProducao(tenantId);
    const vigencia = vigenciaFromPeriodo(formData.periodo_mes, formData.periodo_ano, startDay);

    const { data, error } = await supabase
      .from('desempenho_config')
      .insert({
        tenant_id: tenantId,
        profile_id: formData.profile_id ?? null,
        grupo: formData.grupo,
        tipo_desempenho: formData.tipo_desempenho,
        meta_valor: formData.meta_valor,
        meta_reunioes: formData.meta_reunioes,
        meta_fixacao: formData.meta_fixacao,
        comissao_percentual_base: formData.comissao_percentual_base,
        comissao_bonus_percentual: formData.comissao_bonus_percentual,
        comissao_primeira_parcela_percentual:
          formData.comissao_primeira_parcela_percentual,
        comissao_parcelas_2_a_5_percentual:
          formData.comissao_parcelas_2_a_5_percentual,
        comissao_gerente_pos_percentual:
          formData.comissao_gerente_pos_percentual,
        periodo_mes: formData.periodo_mes,
        periodo_ano: formData.periodo_ano,
        data_inicio: vigencia.data_inicio,
        data_fim: vigencia.data_fim,
        ativo: formData.ativo,
        observacao: formData.observacao,
      })
      .select('*')
      .single();

    if (error) throw error;

    return this.parseRow(data);
  }

  /**
   * Atualizar configuração existente
   */
  async atualizar(
    id: string,
    formData: Partial<DesempenhoConfigFormData>
  ): Promise<DesempenhoConfig> {
    const existing = await this.buscar(id);
    const startDay = await this.getDiaInicioProducao(existing.tenant_id);
    const vigencia =
      formData.periodo_mes !== undefined && formData.periodo_ano !== undefined
        ? vigenciaFromPeriodo(formData.periodo_mes, formData.periodo_ano, startDay)
        : null;

    const { data, error } = await supabase
      .from('desempenho_config')
      .update({
        ...('profile_id' in formData && { profile_id: formData.profile_id ?? null }),
        ...(formData.grupo && { grupo: formData.grupo }),
        ...(formData.tipo_desempenho && { tipo_desempenho: formData.tipo_desempenho }),
        ...(formData.meta_valor !== undefined && { meta_valor: formData.meta_valor }),
        ...(formData.meta_reunioes !== undefined && { meta_reunioes: formData.meta_reunioes }),
        ...(formData.meta_fixacao !== undefined && { meta_fixacao: formData.meta_fixacao }),
        ...(formData.comissao_percentual_base !== undefined && {
          comissao_percentual_base: formData.comissao_percentual_base,
        }),
        ...('comissao_bonus_percentual' in formData && {
          comissao_bonus_percentual: formData.comissao_bonus_percentual ?? null,
        }),
        ...('comissao_primeira_parcela_percentual' in formData && {
          comissao_primeira_parcela_percentual:
            formData.comissao_primeira_parcela_percentual ?? null,
        }),
        ...('comissao_parcelas_2_a_5_percentual' in formData && {
          comissao_parcelas_2_a_5_percentual:
            formData.comissao_parcelas_2_a_5_percentual ?? null,
        }),
        ...('comissao_gerente_pos_percentual' in formData && {
          comissao_gerente_pos_percentual:
            formData.comissao_gerente_pos_percentual ?? null,
        }),
        ...(formData.periodo_mes !== undefined && { periodo_mes: formData.periodo_mes }),
        ...(formData.periodo_ano !== undefined && { periodo_ano: formData.periodo_ano }),
        ...(vigencia && {
          data_inicio: vigencia.data_inicio,
          data_fim: vigencia.data_fim,
        }),
        ...(formData.ativo !== undefined && { ativo: formData.ativo }),
        ...(formData.observacao !== undefined && { observacao: formData.observacao }),
      })
      .eq('id', Number(id))
      .select('*')
      .single();

    if (error) throw error;

    return this.parseRow(data);
  }

  /**
   * Deletar configuração
   */
  async deletar(id: string): Promise<void> {
    const { error } = await supabase
      .from('desempenho_config')
      .delete()
      .eq('id', Number(id));

    if (error) throw error;
  }

  /**
   * Ativar/desativar configuração
   */
  async toggleAtivo(id: string, ativo: boolean): Promise<DesempenhoConfig> {
    return this.atualizar(id, { ativo });
  }

  async listarMembros(tenantId: number): Promise<DesempenhoMembro[]> {
    const { data, error } = await supabase
      .from('profiles')
      .select('id, first_name, last_name, role, tenant_id, status')
      .eq('tenant_id', tenantId)
      .is('deleted_at', null)
      .order('first_name', { ascending: true });

    if (error) throw error;

    return (data ?? []).map(row => ({
      id: row.id,
      nome: [row.first_name, row.last_name].filter(Boolean).join(' ') || 'Usuário sem nome',
      role: row.role || 'USER',
      tenant_id: Number(row.tenant_id),
      status: row.status ?? undefined,
    }));
  }

  async salvarFaixas(configId: string, faixas: DesempenhoFaixaFormData[]): Promise<void> {
    const configIdNum = Number(configId);

    const { error: deleteError } = await (supabase as any)
      .from('desempenho_faixa')
      .delete()
      .eq('desempenho_config_id', configIdNum);

    if (deleteError) throw deleteError;
    if (faixas.length === 0) return;

    const { error: insertError } = await (supabase as any).from('desempenho_faixa').insert(
      faixas.map(faixa => ({
        desempenho_config_id: configIdNum,
        nivel: faixa.nivel,
        classificacao: faixa.classificacao,
        ordem: faixa.ordem,
        meta_valor: faixa.meta_valor ?? null,
        meta_reunioes: faixa.meta_reunioes ?? null,
        meta_fixacao: faixa.meta_fixacao ?? null,
        bonus_percentual: faixa.bonus_percentual ?? null,
      }))
    );

    if (insertError) throw insertError;
  }

  async listarFaixasPorConfigs(configIds: string[]): Promise<Record<string, DesempenhoFaixa[]>> {
    if (configIds.length === 0) return {};

    const numericIds = configIds.map(Number);
    const { data, error } = await (supabase as any)
      .from('desempenho_faixa')
      .select('*')
      .in('desempenho_config_id', numericIds)
      .order('ordem', { ascending: true });

    if (error) throw error;

    const map: Record<string, DesempenhoFaixa[]> = {};
    for (const row of data || []) {
      const faixa = this.parseFaixaRow(row as Record<string, unknown>);
      const key = faixa.desempenho_config_id;
      map[key] = map[key] ?? [];
      map[key].push(faixa);
    }
    return map;
  }

  async criarComFaixas(
    formData: DesempenhoConfigFormData,
    tenantId: number,
    faixas?: DesempenhoFaixaFormData[]
  ): Promise<DesempenhoConfig> {
    const payload = faixas?.length
      ? { ...formData, ...resumoMetaFromFaixas(formData.tipo_desempenho, faixas), comissao_bonus_percentual: undefined }
      : formData;

    const config = await this.criar(payload, tenantId);
    if (faixas?.length) {
      await this.salvarFaixas(config.id, faixas);
    }
    return config;
  }

  async atualizarComFaixas(
    id: string,
    formData: Partial<DesempenhoConfigFormData>,
    faixas?: DesempenhoFaixaFormData[],
    tipo?: TipoDesempenho
  ): Promise<DesempenhoConfig> {
    const payload =
      faixas?.length && tipo
        ? {
            ...formData,
            ...resumoMetaFromFaixas(tipo, faixas),
            comissao_bonus_percentual: undefined,
          }
        : formData;

    const config = await this.atualizar(id, payload);
    if (faixas) {
      await this.salvarFaixas(id, faixas);
    }
    return config;
  }

  async listarFaixas(configId: string): Promise<DesempenhoFaixa[]> {
    const { data, error } = await supabase.rpc('get_desempenho_faixas', {
      p_desempenho_config_id: Number(configId),
    });
    if (error) throw error;
    return (data || []).map((row: Record<string, unknown>) => this.parseFaixaRow(row));
  }

  async buscarFaixasVigente(
    tenantId: number,
    tipoDesempenho: TipoDesempenho,
    grupo?: GrupoDesempenho,
    refDate?: string,
    profileId?: string
  ): Promise<DesempenhoFaixa[]> {
    const rpcName = profileId
      ? 'get_desempenho_faixas_aplicavel'
      : 'get_desempenho_faixas_vigente';
    const { data, error } = await (supabase as any).rpc(rpcName, {
      p_tenant_id: tenantId,
      p_tipo_desempenho: tipoDesempenho,
      p_grupo: grupo ?? null,
      ...(profileId && { p_profile_id: profileId }),
      p_ref_date: refDate ?? new Date().toISOString().split('T')[0],
    });
    if (error) throw error;
    return (data || []).map((row: Record<string, unknown>) => this.parseFaixaRow(row));
  }

  async classificarFaixaAtingida(
    configId: string,
    realizadoValor = 0,
    realizadoReunioes = 0,
    realizadoFixacao = 0
  ): Promise<DesempenhoFaixa | null> {
    const { data, error } = await supabase.rpc('classificar_faixa_atingida', {
      p_desempenho_config_id: Number(configId),
      p_realizado_valor: realizadoValor,
      p_realizado_reunioes: realizadoReunioes,
      p_realizado_fixacao: realizadoFixacao,
    });
    if (error) throw error;
    const row = Array.isArray(data) ? data[0] : data;
    return row ? this.parseFaixaRow(row as Record<string, unknown>) : null;
  }

  // Helper: converter datas
  private parseFaixaRow(row: Record<string, unknown>): DesempenhoFaixa {
    return {
      id: String(row.id),
      desempenho_config_id: String(row.desempenho_config_id),
      nivel: row.nivel as DesempenhoFaixa['nivel'],
      classificacao: String(row.classificacao),
      ordem: Number(row.ordem),
      meta_valor: row.meta_valor != null ? Number(row.meta_valor) : undefined,
      meta_reunioes: row.meta_reunioes != null ? Number(row.meta_reunioes) : undefined,
      meta_fixacao: row.meta_fixacao != null ? Number(row.meta_fixacao) : undefined,
      bonus_percentual: row.bonus_percentual != null ? Number(row.bonus_percentual) : undefined,
      created_at: new Date(row.created_at as string),
      updated_at: new Date(row.updated_at as string),
    };
  }

  private parseRow(row: any): DesempenhoConfig {
    return {
      ...row,
      id: String(row.id),
      data_inicio: new Date(row.data_inicio),
      data_fim: row.data_fim ? new Date(row.data_fim) : undefined,
      created_at: new Date(row.created_at),
      updated_at: new Date(row.updated_at),
    };
  }
}

export const desempenhoService = new DesempenhoService();
